import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, FolderTree } from "lucide-react";

const EMPTY = { name: "", parent_id: null, domain: "both", slug: "", description: "", order: 0 };

export default function AdminTaxonomyPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/taxonomy"); setItems(r.data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byParent = useMemo(() => {
    const m = new Map();
    for (const n of items) {
      const p = n.parent_id || "__root__";
      if (!m.has(p)) m.set(p, []);
      m.get(p).push(n);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));
    return m;
  }, [items]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing === "new") await api.post("/admin/taxonomy", form);
      else await api.put(`/admin/taxonomy/${editing}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this taxonomy node? (Must have no children + no linked ideas/datasets.)")) return;
    try {
      await api.delete(`/admin/taxonomy/${id}`); toast.success("Deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const flatRows = useMemo(() => {
    const rows = [];
    const walk = (parentId, depth) => {
      const kids = byParent.get(parentId) || [];
      for (const n of kids) {
        rows.push({ node: n, depth });
        walk(n.id, depth + 1);
      }
    };
    walk("__root__", 0);
    return rows;
  }, [byParent]);

  const renderRow = (n, depth) => (
    <div key={n.id}>
      <div className="flex items-center gap-2 py-1 hover:bg-slate-50 rounded-sm group" style={{ paddingLeft: depth * 16 + 8 }} data-testid={`tax-row-${n.id}`}>
        <FolderTree className="w-3.5 h-3.5 text-kb-ink-muted shrink-0" />
        <span className="text-sm text-kb-ink flex-1 min-w-0 truncate">{n.name}</span>
        <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light shrink-0">{n.domain}</span>
        <button onClick={() => { setEditing("new"); setForm({ ...EMPTY, parent_id: n.id, domain: n.domain }); }}
          className="opacity-0 group-hover:opacity-100 p-1 text-kb-primary" title="Add child" data-testid={`tax-row-${n.id}-add-child`}>
          <Plus className="w-3 h-3" />
        </button>
        <button onClick={() => { setEditing(n.id); setForm({ ...EMPTY, ...n }); }}
          className="opacity-0 group-hover:opacity-100 p-1 text-kb-ink-muted" data-testid={`tax-row-${n.id}-edit`}>
          <Edit3 className="w-3 h-3" />
        </button>
        <button onClick={() => remove(n.id)}
          className="opacity-0 group-hover:opacity-100 p-1 text-rose-600" data-testid={`tax-row-${n.id}-delete`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-taxonomy-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">TAXONOMY</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Manage the <em className="italic text-kb-primary">domain hierarchy</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Add and reorder the shared taxonomy used by Research Ideas and Datasets. Domain → Subdomain → Topic → Research Area.</p>
        </div>
        <button onClick={() => { setEditing("new"); setForm(EMPTY); }} className="kb-btn-primary text-sm" data-testid="tax-new-btn"><Plus className="w-3.5 h-3.5" /> New node</button>
      </div>

      {editing && (
        <div className="mt-6 kb-card p-6" data-testid="tax-editor">
          <div className="flex items-center justify-between mb-3">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">{editing === "new" ? "NEW NODE" : "EDITING"}</div>
            <button onClick={() => setEditing(null)} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="kb-input text-sm sm:col-span-2" placeholder="Name *"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tax-name-input" />
            <select className="kb-input text-sm" value={form.parent_id || ""}
              onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
              data-testid="tax-parent-select">
              <option value="">— root level —</option>
              {items.filter((n) => n.id !== editing).map((n) => (
                <option key={n.id} value={n.id}>{(n.path || [n.name]).join(" › ")}</option>
              ))}
            </select>
            <select className="kb-input text-sm" value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="tax-domain-select">
              <option value="both">Both (Research Ideas + Datasets)</option>
              <option value="research_ideas">Research Ideas only</option>
              <option value="datasets">Datasets only</option>
            </select>
            <input type="number" className="kb-input text-sm" placeholder="Order" value={form.order}
              onChange={(e) => setForm({ ...form, order: (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || 0)) })} />
            <input className="kb-input text-sm sm:col-span-2" placeholder="Description (optional)"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} className="kb-btn-primary text-sm" data-testid="tax-save-btn">Save</button>
            <button onClick={() => setEditing(null)} className="kb-btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-8 kb-card p-4">
        {loading ? <div className="kb-mono text-xs text-kb-ink-muted">Loading…</div> :
          flatRows.length === 0 ? <div className="kb-mono text-xs text-kb-ink-muted py-6 text-center">No nodes yet.</div> :
          flatRows.map(({ node, depth }) => renderRow(node, depth))}
      </div>
    </div>
  );
}
